import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from '../Navigation/Sidebar';
import TopBar from '../Navigation/TopBar';
import DynamicLogo from '../Logo/DynamicLogo';
import { UserRole } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../utils/roles';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import AIChatBubble from '../AI/AIChatBubble';
import { useLanguage } from '../../contexts/LanguageContext';
import './MainLayout.css';

// ============================================================
// FEATURE FLAG: Tắt AI Chat để phát triển thêm
// true  = hiển thị AI chat bubble
// false = ẩn AI chat (tạm tắt)
// TODO: Đổi thành true khi AI đã sẵn sàng public
// ============================================================
const AI_CHAT_ENABLED = false;

const MainLayout = ({ children }: { children: React.ReactNode }) => {
  usePushNotifications();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user: currentUser } = useAuth(); // Use auth context
  const { language } = useLanguage();

  // ... (keep existing state logic if needed, or rely on ... logic)
  const location = useLocation();

  // Kiểm tra quyền hiển thị trang Employees
  const canAccessEmployees = () => {
    if (!currentUser) return false;

    // UserRole values now match AuthContext ROLES
    return [
      ROLES.OWNER,
      ROLES.MANAGER,
      ROLES.STAFF,
      ROLES.ADMIN
    ].includes(currentUser.role);
  };

  // ... (keep checking permissions)
  const canAddEmployees = () => {
    if (!currentUser) return false;
    return [ROLES.OWNER, ROLES.MANAGER, ROLES.ADMIN].includes(currentUser.role);
  };

  const canManageEmployeeRoles = () => {
    if (!currentUser) return false;
    return currentUser.role === ROLES.ADMIN;
  };

  const toggleSidebar = () => {
    if (isMobile) {
      setIsSidebarOpen(!isSidebarOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  const closeSidebar = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  // Check mobile logic
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);

      if (mobile) {
        setIsSidebarOpen(false);
      } else {
        setIsCollapsed(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto close sidebar on route change (mobile)
  useEffect(() => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  const getAppLayoutClass = () => {
    if (isMobile) {
      return isSidebarOpen ? 'sidebar-open' : '';
    } else {
      return isCollapsed ? 'sidebar-collapsed' : '';
    }
  };

  return (
    <div className={`app-layout ${getAppLayoutClass()}`}>
      <Sidebar
        isCollapsed={isCollapsed}
        isMobile={isMobile}
        isSidebarOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        closeSidebar={closeSidebar}
        currentUser={currentUser as any}
        canAccessEmployees={canAccessEmployees()}
      />

      <div className="main-content">
        <TopBar
          toggleSidebar={toggleSidebar}
          currentUser={currentUser as any}
        />

        <section className="app-cover">
          <DynamicLogo />
        </section>

        <div className="content">
          {children}
        </div>

        {/* ── Author Signature Footer ── */}
        <footer className="app-footer">
          <div className="app-footer-inner">
            <span className="app-footer-copy">© {new Date().getFullYear()}</span>
            <span className="app-footer-brand">Phanh</span>
            <span className="app-footer-sep">·</span>
            <span className="app-footer-author">
              Designed &amp; Built by&nbsp;
              <strong className="app-footer-name">Hoàng Phi Anh</strong>
              <span className="app-footer-alias">&nbsp;(Noland Hoang)</span>
            </span>
          </div>
        </footer>
      </div>

      {/* ── AI Assistant Chat Bubble ── */}
      {AI_CHAT_ENABLED && <AIChatBubble isPublic={false} language={language} />}
    </div>
  );
};

export default MainLayout;